import React, {
  FC,
  useCallback,
  useContext,
  useEffect,
  useMemo,
  useState,
} from "react";
import { useRouter } from "next/router";
import { ApolloError } from "@apollo/client";
import { format } from "date-fns";
import { CheckoutContext } from "v3/context/checkout";
import { TenantContext } from "v3/context/tenant";
import { CheckoutErrorContext } from "v3/context/checkoutError";
import { PaymentContext } from "v3/context/payment";
import { useCurrentAppId } from "v3/hooks/useCurrentAppId";
import {
  useSaveShippingOptionsMutation,
  useSubmitOrderMutation,
} from "v3/services/graphql/generated";
import {
  logContinueToPurchaseConfirmation,
  logOrderPurchaseError,
  logOrderReview,
  logPurchaseComplete,
  logPurchaseEmailOptIn,
} from "v3/services/analytics";
import { getPaymentDetailsForReviewStep } from "v3/helpers/payment";
import { getSortedTermsByLocation } from "@pepdirect/v3/ui/TermsAndConditions";
import { ReviewSection } from "v3/components/checkout/ReviewSection";
import { DEFAULT_REQUESTED_DELIVERY_DATE } from "v3/components/checkout/RequestedDeliveryDate";
import { getTimeZoneFromBrowser } from "v3/helpers/localization";
import { EMBEDDED_GIFT_CARD_TYPES } from "v3/types/payment";

interface BraintreeReviewStepProps {
  editPayment: () => void;
  editShip: () => void;
}

export const BraintreeReviewStep: FC<BraintreeReviewStepProps> = ({
  editPayment,
  editShip,
}) => {
  const [saveRequestedDeliveryDate] = useSaveShippingOptionsMutation();
  const [submitOrder] = useSubmitOrderMutation();
  const router = useRouter();
  const { appId } = useCurrentAppId();

  const { apiBaseUrl, checkoutSession, setIsLoading } =
    useContext(CheckoutContext);
  const {
    availablePaymentMethods,
    clearPaymentDataOnEdit,
    newCreditCardBraintreeResponse,
    newPayPalResponse,
    newApplePayResponse,
    paymentNonce,
    paymentType,
  } = useContext(PaymentContext);
  const { tenant } = useContext(TenantContext);

  const { handleCheckoutErrors, clearAllErrors } =
    useContext(CheckoutErrorContext);

  useEffect(() => {
    const logEvents = () => {
      if (!tenant || !checkoutSession) return;
      logContinueToPurchaseConfirmation(appId, checkoutSession);
      logOrderReview(checkoutSession.cart);
    };
    logEvents();
  }, [appId, checkoutSession, tenant]);

  const { contactInfo, id, payment, requestedDeliveryDate, user } =
    checkoutSession || {};
  const { shippingAddress } = contactInfo || {};
  const { billingAddress } = payment || {};
  const giftCard = useMemo(() => {
    // do not display gift card details if it does not cover the full amount
    if (checkoutSession?.price.total) {
      return null;
    }
    return checkoutSession?.giftCards?.find(
      (item) => item?.type === EMBEDDED_GIFT_CARD_TYPES.BALANCE
    );
  }, [checkoutSession?.giftCards, checkoutSession?.price.total]);
  const [finalRequestedDeliveryDate, setFinalRequestedDeliveryDate] =
    useState<string>(requestedDeliveryDate || "");
  const showRequestedDelivery =
    tenant?.userExperience?.hasRequestedDeliveryDate;

  // if skipToReview, handle undefined delivery date if tenant has requested delivery date enabled
  useEffect(() => {
    const handleRequestedDeliveryDate = async () => {
      if (showRequestedDelivery && !finalRequestedDeliveryDate) {
        try {
          const { data } = await saveRequestedDeliveryDate({
            variables: {
              id,
              input: {
                requestedDeliveryDate: format(
                  DEFAULT_REQUESTED_DELIVERY_DATE,
                  "yyyy-MM-dd"
                ),
              },
            },
          });
          if (data?.saveShippingOptions?.requestedDeliveryDate) {
            setFinalRequestedDeliveryDate(
              data.saveShippingOptions.requestedDeliveryDate
            );
          }
        } catch (e) {
          handleCheckoutErrors(e);
        } finally {
          setIsLoading(false);
        }
      } else {
        setIsLoading(false);
      }
    };
    handleRequestedDeliveryDate();
  }, [
    finalRequestedDeliveryDate,
    handleCheckoutErrors,
    id,
    saveRequestedDeliveryDate,
    setIsLoading,
    showRequestedDelivery,
  ]);

  const paymentDetails = useMemo(() => {
    return getPaymentDetailsForReviewStep({
      availablePaymentMethods,
      billingAddress,
      newApplePayResponse,
      newCreditCardBraintreeResponse,
      newPayPalResponse,
      paymentType,
      savedToken: checkoutSession?.payment?.token,
      giftCard,
    });
  }, [
    availablePaymentMethods,
    billingAddress,
    checkoutSession?.payment?.token,
    giftCard,
    newApplePayResponse,
    newCreditCardBraintreeResponse,
    newPayPalResponse,
    paymentType,
  ]);

  const sortedTerms = useMemo(() => {
    return getSortedTermsByLocation({
      location: "review",
      terms: tenant?.brandConfig.customTexts || [],
    });
  }, [tenant]);

  const validateTerms = useCallback(
    (values: Record<string, boolean>) => {
      const errors: Record<string, string> = {};
      sortedTerms?.forEach((term) => {
        if (!values[term.type]) {
          errors[term.type] = `${term.type} is required`;
        }
      });
      return errors;
    },
    [sortedTerms]
  );

  const handleEditPayment = () => {
    clearPaymentDataOnEdit();
    editPayment();
  };

  const handleSubmitOrder = async () => {
    try {
      clearAllErrors();

      setIsLoading(true);
      const { data } = await submitOrder({
        variables: {
          id,
          input: {
            paymentNonce,
            preferredTimezone: getTimeZoneFromBrowser(),
          },
        },
      });

      const { submitOrder: submittedOrder } = data || {};

      if (submittedOrder?.id) {
        logPurchaseComplete(
          checkoutSession?.cart,
          appId,
          apiBaseUrl,
          submittedOrder
        );
        const { contactInfo } = submittedOrder;
        const emailForOptIn = !!contactInfo?.emailOptIn && contactInfo.email;
        if (emailForOptIn) logPurchaseEmailOptIn(emailForOptIn);
        // redirect to the order confirmation page
        const href = `/v3/status/${submittedOrder.id}/${appId}`;
        await router.push(href);
      }
    } catch (e) {
      handleCheckoutErrors(e);

      if (e instanceof ApolloError && e.networkError) {
        logOrderPurchaseError(e, contactInfo?.email, user?.id);
      }
    } finally {
      setIsLoading(false);
    }
  };

  // something went wrong
  if (!shippingAddress || !paymentDetails || !contactInfo) return null;

  return (
    <ReviewSection
      editPayment={handleEditPayment}
      editShip={editShip}
      onSubmit={handleSubmitOrder}
      paymentDetails={paymentDetails}
      terms={sortedTerms}
      validateTerms={validateTerms}
    />
  );
};
